import C from "@/components/sidebar/StandardSidebar.module.css";

import { localeCompareBy } from "@/components/data/comparers.ts";
import { getFilteredItems } from "@/components/data/getFilteredItems.ts";
import { getSortedItems, type SimpleSortField } from "@/components/data/getSortedItems.ts";
import { EmptyItem } from "@/components/sidebar/EmptyItem.tsx";
import { PriceBlock } from "@/components/sidebar/PriceBlock.tsx";
import { ListResult, SidebarList, type SidebarListState } from "@/components/sidebar/SidebarList.tsx";
import { StandardBlock } from "@/components/sidebar/StandardSidebar.tsx";
import { ResourceCreateLinks } from "@/modules/api/resourceCreateLinks.ts";
import { allAssignedVmsQueryAtom, type VirtualMachineWithAssigned } from "@/modules/compute/joinAssignedQuery.ts";
import { vmLink } from "@/modules/compute/links.ts";
import { MiniMetricChart } from "@/modules/compute/monitor/MetricChart.tsx";
import { cpuMetric } from "@/modules/compute/monitor/metricIds.ts";
import { OsImageIcon } from "@/modules/compute/os/OsImageIcon.tsx";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { datetimeToDate } from "@warrenio/api-spec/conversion";
import { useAtomValue } from "jotai/react";

const sortFields = [
    {
        id: "name",
        title: <Trans>Name</Trans>,
        compare: localeCompareBy((i) => i.name),
    },
    { id: "created_at", title: <Trans>Created date</Trans>, getValue: (i) => datetimeToDate(i.created_at).getTime() },
    { id: "location", title: <Trans>Location</Trans>, getValue: (i) => i.location },
    { id: "price", title: <Trans>Price</Trans>, getValue: (i) => i.price.hourly },
] satisfies SimpleSortField<VirtualMachineWithAssigned>[];

export default function VmList() {
    const { t } = useLingui();

    return (
        <SidebarList
            data={(props) => <VmData {...props} />}
            title={t`Compute`}
            sortFields={sortFields}
            actionButton={{
                title: t`Create new`,
                action: ResourceCreateLinks.virtual_machine,
            }}
            listId="compute"
        />
    );
}

function VmData({ queryString, sortId, sortAsc }: SidebarListState) {
    const query = useAtomValue(allAssignedVmsQueryAtom);

    let vmItems = getFilteredItems(queryString, [...query.data.values()], (item) => [item.name, item.uuid]);
    vmItems = getSortedItems(sortFields, { sortId, sortAsc }, vmItems);

    return (
        <ListResult
            items={vmItems.map((obj) => (
                <VmBlock key={obj.uuid} item={obj} />
            ))}
            isSearching={!!queryString}
            query={query}
            emptyItem={
                <EmptyItem icon="jp-compute-cloud-icon">
                    <Trans>Create a new resource and get your services running in minutes.</Trans>
                </EmptyItem>
            }
        />
    );
}

export function VmBlock({ item }: { item: VirtualMachineWithAssigned }) {
    const {
        location,
        name,
        uuid,
        os_name,
        status,
        assignedPublicIp,
        private_ipv4,
        price,
        billing_account: baId,
    } = item;

    return (
        <StandardBlock link={vmLink(item)}>
            <div className={C.StandardBlockIcon}>
                <OsImageIcon os_name={os_name} className="size-1rem" />
            </div>

            <div className={cn(C.StandardBlockName, "text-ellipsis")}>
                <b className="text-ellipsis">{name}</b>
                <div className="text-ellipsis color-primary">
                    {assignedPublicIp?.address ?? private_ipv4} / {location}
                </div>
            </div>

            <div className={cn(C.StandardBlockChart, status === "running" && C.Filter)}>
                {status === "running" && <MiniMetricChart host={uuid} metric={cpuMetric} location={location} />}
                {status === "stopped" && <div className={C.Stopped} />}
            </div>

            <PriceBlock price={price} billingAccountId={baId} />
        </StandardBlock>
    );
}
